import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import CoralMetadataForm from '../components/CoralMetadataForm';
import ChildFragmentsBatch from '../components/ChildFragmentsBatch';
import ParentCoralSummary from '../components/ParentCoralSummary';
import { CoralFormData, BatchItem, CoralResponse } from '../types';
import { getCoral } from '../services/api';
import { accent } from '../theme';

const CreateCorals: React.FC = () => {
  const { parentCoralId } = useParams<{ parentCoralId: string }>();
  const [parentCoral, setParentCoral] = useState<CoralResponse | null>(null);
  const [parentLoading, setParentLoading] = useState(Boolean(parentCoralId));
  const [parentError, setParentError] = useState<string | null>(null);
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  // Disabled individual fragment editing - all fragments use shared metadata
  // const [selectedRfid, setSelectedRfid] = useState<string | null>(null);
  const [globalFormData, setGlobalFormData] = useState<CoralFormData>({
    speciesCode: '',
    cutDate: '',
    condition: 'ALIVE',
  });

  useEffect(() => {
    if (!parentCoralId) {
      setParentCoral(null);
      setParentLoading(false);
      setParentError(null);
      return;
    }

    let active = true;
    const loadParent = async () => {
      try {
        setParentLoading(true);
        const response = await getCoral(parentCoralId);
        if (active) {
          setParentCoral(response);
          setParentError(null);
        }
      } catch (error: any) {
        if (active) {
          setParentCoral(null);
          setParentError(error?.message || 'Failed to load parent coral');
        }
      } finally {
        if (active) {
          setParentLoading(false);
        }
      }
    };

    void loadParent();
    return () => {
      active = false;
    };
  }, [parentCoralId]);

  const handleScan = useCallback(
    (code: string) => {
      const trimmedCode = code.trim();

      // Check if RFID already exists in the batch (case-insensitive comparison)
      const existingItem = batchItems.find(
        (item) => item.rfid.toLowerCase() === trimmedCode.toLowerCase()
      );
      if (existingItem) {
        // Return false to indicate duplicate RFID
        return false;
      }

      // Create new batch item with default metadata from global form
      const newItem: BatchItem = {
        rfid: trimmedCode,
        metadata: { ...globalFormData },
      };
      setBatchItems((items) => [newItem, ...items]); // newest first

      // Return true to indicate successful addition
      return true;
    },
    [globalFormData, batchItems]
  );

  const removeFromQueue = useCallback(
    (rfid: string) => {
      setBatchItems((items) => items.filter((item) => item.rfid !== rfid));
      // Disabled individual fragment editing - no selection to clear
      // if (selectedRfid === rfid) {
      //   setSelectedRfid(null);
      // }
    },
    [] // Removed selectedRfid dependency since we're not using selection
  );

  // Disabled individual fragment editing - all fragments use shared metadata
  // const handleBatchItemUpdate = useCallback((rfid: string, metadata: CoralFormData) => {
  //   setBatchItems((items) =>
  //     items.map((item) => (item.rfid === rfid ? { ...item, metadata } : item))
  //   );
  // }, []);

  const handleBatchComplete = useCallback((successCount: number, errorCount: number) => {
    if (successCount > 0) {
      // Clear the batch items after successful creation
      setBatchItems([]);
      // Disabled individual fragment editing - no selection to clear
      // setSelectedRfid(null);
    }
  }, []);

  // Disabled individual fragment editing - all fragments use shared metadata
  // const selectedBatchItem = selectedRfid
  //   ? batchItems.find((item) => item.rfid === selectedRfid)
  //   : undefined;

  // const handleEditComplete = useCallback(() => {
  //   // Clear selection after successful update to allow editing next fragment
  //   setSelectedRfid(null);
  // }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 3, maxWidth: 1200, mx: 'auto' }}>
      {parentCoralId && (
        <Button
          component={RouterLink}
          to={`/coral/view/${encodeURIComponent(parentCoralId)}`}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 1 }}
        >
          Back to Parent Coral
        </Button>
      )}
      <Typography variant="h4" gutterBottom>
        {parentCoralId ? 'Create Child Fragments' : 'Create New Coral Fragments'}
      </Typography>
      {parentLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading parent coral...</Typography>
        </Stack>
      )}
      {parentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {parentError}
        </Alert>
      )}
      {parentCoral && <ParentCoralSummary coral={parentCoral} />}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 500 }}>
        {/* New Fragment Batch - takes up 1/3 of the width */}
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              width: '100%',
              boxShadow: 1,
              display: 'flex',
              flexDirection: 'column',
              ...accent.coral,
            }}
          >
            <Typography variant="h6" gutterBottom>
              New Fragment Batch
            </Typography>
            <Box sx={{ flexGrow: 1, display: 'flex' }}>
              <ChildFragmentsBatch
                items={batchItems}
                onRemove={removeFromQueue}
                parentCoralId={parentCoralId}
                creationDisabled={Boolean(parentCoralId) && !parentCoral?.rfid}
                // Disabled individual fragment editing - all fragments use shared metadata
                // selectedRfid={selectedRfid || undefined}
                // onSelect={setSelectedRfid}
                onBatchComplete={handleBatchComplete}
              />
            </Box>
          </Paper>
        </Box>

        {/* New Child Fragment - takes up 2/3 of the width */}
        <Box sx={{ flex: 2, display: 'flex' }}>
          <Paper variant="outlined" sx={{ p: 2, width: '100%', boxShadow: 1, ...accent.navy }}>
            <Typography variant="h6" gutterBottom>
              Coral Metadata
            </Typography>
            <CoralMetadataForm
              onFormDataChange={setGlobalFormData}
              // Disabled individual fragment editing - all fragments use shared metadata
              // selectedBatchItem={selectedBatchItem}
              // onBatchItemUpdate={handleBatchItemUpdate}
              // onEditComplete={handleEditComplete}
              onScan={handleScan}
              hasItemsInBatch={batchItems.length > 0}
              batchItems={batchItems}
            />
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateCorals;
